/* eslint-disable indent */
import React, { useEffect, useState } from 'react';
import { FormInstance } from 'antd/lib/form';
import { Col, Form, Input, Row, Switch, Tag, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import Validations from '../../utils/Validations';
import { APIForwarder, ClientFeatureConfig } from '../../types/Feature';
import FeatureStatusInput from '../Shared/Inputs/FeatureStatusInput';

const cardHeaderTitleStyle = { fontSize: '18px' };

interface ApiForwarderInterfaceProps {
    formInstance: FormInstance;
    apiForwarderConfig?: ClientFeatureConfig<APIForwarder>;
    isDetailMode?: boolean;
    enableControls?: boolean;
    isWebhookEnabled: boolean;
    onFeatureStatusChange: (val: boolean) => void;
}

const APIForwarderFormSection: React.FC<ApiForwarderInterfaceProps> = (props: ApiForwarderInterfaceProps) => {
    const {
        formInstance,
        apiForwarderConfig,
        isWebhookEnabled,
        isDetailMode,
        enableControls,
        onFeatureStatusChange,
    } = props;

    const [isFeatureDisabled, setIsFeatureDisabled] = useState<boolean>(false);

    useEffect(() => {
        if (apiForwarderConfig) {
            formInstance.setFields([
                { name: 'apiforwarderEnabled', value: apiForwarderConfig.config.enabled },
                { name: 'apiforwarderApiName', value: apiForwarderConfig.config.apiName },
                { name: 'apiforwarderBaseurl', value: apiForwarderConfig.config.baseUrl },
                { name: 'apiforwarderAuthUrl', value: apiForwarderConfig.config.authUrl },
                { name: 'apiforwarderLogin', value: apiForwarderConfig.config.login },
                { name: 'apiforwarderClientSecret', value: apiForwarderConfig.config.clientSecret },
                { name: 'apiforwarderPassword', value: apiForwarderConfig.config.password },
                { name: 'apiforwarderIsFlex', value: apiForwarderConfig.config.isFlex },
                { name: 'apiforwarderUseAddressQualificator', value: apiForwarderConfig.config.useAddressQualificator },
                { name: 'apiforwarderSlackChannelId', value: apiForwarderConfig.config.slackChannelId },
            ]);
            setIsFeatureDisabled(!apiForwarderConfig.config.enabled);
        }
    }, [apiForwarderConfig]);

    useEffect(() => {
        if (!isWebhookEnabled) {
            setIsFeatureDisabled(true);
            formInstance.setFieldsValue({ apiforwarderEnabled: isWebhookEnabled && !isFeatureDisabled });
        }
    }, [isWebhookEnabled]);

    return (
        <div className="card border-0" id="webhook-section">
            <div className="card-header border-bottom d-flex justify-content-between align-items-center bg-white">
                <p className="m-0 p-0 h5" style={cardHeaderTitleStyle}>
                    Custom API & Webhook Forwarder
                </p>
                <div className="d-inline-flex align-items-center">
                    <span className="mr-2 d-none d-sm-block">Feature is:</span>
                    <FeatureStatusInput
                        name="apiforwarderEnabled"
                        id="apiforwarder_enabled"
                        isDetailMode={isDetailMode}
                        enabled={apiForwarderConfig?.config.enabled}
                        isFeatureDisabled={isFeatureDisabled}
                        onChange={(value): void => {
                            setIsFeatureDisabled(!value);
                            onFeatureStatusChange(value);
                        }}
                        enableControls={isWebhookEnabled}
                    />
                </div>
            </div>
            <div className="card-body">
                <Row className="h6 text-secondary mb-3">Parameters:</Row>
                <Row gutter={24}>
                    <Col span={6} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 6, offset: 1 }}>
                        <Form.Item
                            name="apiforwarderApiName"
                            label={<span className="mr-2">API Name</span>}
                            rules={[
                                {
                                    required: !isFeatureDisabled,
                                    message: 'Field API Name is required',
                                },
                            ]}
                        >
                            {isDetailMode ? (
                                <span className="ant-form-text text-secondary">
                                    {apiForwarderConfig?.config.apiName || '-'}
                                </span>
                            ) : (
                                <Input
                                    id="apiforwarderApiName"
                                    className="col-12"
                                    title="Redirect URL"
                                    autoComplete="off"
                                    disabled={isFeatureDisabled || !enableControls}
                                />
                            )}
                        </Form.Item>
                    </Col>
                    <Col span={10} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 10, offset: 3 }}>
                        <Form.Item
                            name="apiforwarderBaseurl"
                            label={
                                // eslint-disable-next-line react/jsx-wrap-multilines
                                <>
                                    <span className="mr-2">Base URL</span>
                                    <Tooltip title="Enter a Base URL (http://api.mydomain.com)">
                                        <InfoCircleOutlined className="text-muted" />
                                    </Tooltip>
                                </>
                            }
                            rules={[
                                {
                                    required: !isFeatureDisabled,
                                    message: 'Field Base URL is required',
                                },
                                {
                                    required: !isFeatureDisabled,
                                    validator: (rule, value, callback): void => {
                                        if (!!isFeatureDisabled || Validations.ValidateURL(value)) callback();
                                        callback('Invalid URL format');
                                    },
                                },
                            ]}
                        >
                            {isDetailMode ? (
                                <span className="ant-form-text text-secondary">
                                    {apiForwarderConfig?.config.baseUrl || '-'}
                                </span>
                            ) : (
                                <Input
                                    className="col-12"
                                    name="apiforwarderBaseUrl"
                                    title="Base URL"
                                    autoComplete="off"
                                    disabled={!!isFeatureDisabled || !enableControls}
                                />
                            )}
                        </Form.Item>
                    </Col>
                </Row>
            </div>
            <div className="card-body">
                <Row className="h6 text-secondary mb-3">Optional configuration:</Row>
                <Row gutter={24}>
                    <Col span={10} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 10, offset: 1 }}>
                        <Form.Item name="apiforwarderAuthUrl" label="Authentication URL">
                            {isDetailMode ? (
                                <span className="ant-form-text text-secondary">
                                    {apiForwarderConfig?.config.authUrl || '-'}
                                </span>
                            ) : (
                                <Input
                                    className="col-12"
                                    name="apiforwarderAuthUrl"
                                    title="Auth URL"
                                    autoComplete="off"
                                    disabled={!!isFeatureDisabled || !enableControls}
                                />
                            )}
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={24}>
                    <Col span={6} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 6, offset: 1 }}>
                        <Form.Item name="apiforwarderLogin" label={<span className="mr-2">Login</span>}>
                            {isDetailMode ? (
                                <span className="ant-form-text text-secondary">
                                    {apiForwarderConfig?.config.login || '-'}
                                </span>
                            ) : (
                                <Input
                                    className="col-12"
                                    name="login"
                                    title="Redirect URL"
                                    autoComplete="off"
                                    disabled={!!isFeatureDisabled || !enableControls}
                                />
                            )}
                        </Form.Item>
                    </Col>
                    <Col span={6} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 6, offset: 3 }}>
                        <Form.Item name="apiforwarderClientSecret" label={<span className="mr-2">Secret</span>}>
                            {isDetailMode ? (
                                <span className="ant-form-text text-secondary">
                                    {apiForwarderConfig?.config.clientSecret || '-'}
                                </span>
                            ) : (
                                <Input
                                    className="col-12"
                                    name="apiforwarderClientSecret"
                                    title="Client secret"
                                    autoComplete="off"
                                    disabled={!!isFeatureDisabled || !enableControls}
                                />
                            )}
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={24}>
                    <Col span={6} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 6, offset: 1 }}>
                        <Form.Item name="apiforwarderPassword" label={<span className="mr-2">Password</span>}>
                            {isDetailMode ? (
                                <span className="ant-form-text text-secondary">
                                    {apiForwarderConfig?.config.password || '-'}
                                </span>
                            ) : (
                                <Input
                                    className="col-12"
                                    name="apiforwarderPassword"
                                    title="Redirect URL"
                                    autoComplete="off"
                                    disabled={!!isFeatureDisabled || !enableControls}
                                />
                            )}
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={24}>
                    <Col span={6} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 6, offset: 1 }}>
                        <Form.Item
                            name="apiforwarderIsFlex"
                            valuePropName="checked"
                            label={<span className="mr-2">Use Flex Features</span>}
                        >
                            {isDetailMode ? (
                                <span className="ant-form-text text-secondary">
                                    {apiForwarderConfig?.config.isFlex ? 'Enabled' : 'Disabled'}
                                </span>
                            ) : (
                                <Switch
                                    title="Toggle Flex Features"
                                    checkedChildren="Enabled"
                                    unCheckedChildren="Disabled"
                                    disabled={!!isFeatureDisabled || !enableControls}
                                />
                            )}
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={24}>
                    <Col span={6} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 6, offset: 1 }}>
                        <Form.Item
                            name="apiforwarderUseAddressQualificator"
                            valuePropName="checked"
                            label={<span className="mr-2">Use Address Qualificator</span>}
                        >
                            {isDetailMode ? (
                                <span className="ant-form-text text-secondary">
                                    {apiForwarderConfig?.config.useAddressQualificator ? 'Enabled' : 'Disabled'}
                                </span>
                            ) : (
                                <Switch
                                    title="Use Address Qualificator"
                                    checkedChildren="Enabled"
                                    unCheckedChildren="Disabled"
                                    disabled={!!isFeatureDisabled || !enableControls}
                                />
                            )}
                        </Form.Item>
                    </Col>
                    <Col span={9} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 6, offset: 1 }}>
                        <Form.Item
                            name="apiforwarderSlackChannelId"
                            label={
                                <>
                                    <span className="mr-2">Slack channel Id</span>
                                    <Tooltip title="If Use Address Qualificator is on, we can send message on address Error on a dedicated Slack Channel. Solutions slack bot MUST BE IN THE CHANNEL">
                                        <InfoCircleOutlined className="text-muted" />
                                    </Tooltip>
                                </>
                            }
                        >
                            {isDetailMode ? (
                                <span className="ant-form-text text-secondary">
                                    {apiForwarderConfig?.config.slackChannelId || '-'}
                                </span>
                            ) : (
                                <Input
                                    className="col-12"
                                    name="apiforwarderSlackChannelId"
                                    title="Slack Channel ID"
                                    autoComplete="off"
                                    disabled={!!isFeatureDisabled || !enableControls}
                                />
                            )}
                        </Form.Item>
                    </Col>
                </Row>
            </div>
        </div>
    );
};

export default APIForwarderFormSection;
